import React from 'react';

const Intro = () => <>
<p>
PRIVACY NOTICE
</p><p>
Last updated November 26, 2022
</p><p>
This privacy notice for TIFA Sports ("Company," "we," "us," or "our"), describes how and why we might collect, store, use, and/or share ("process") your information when you use our services ("Services"), such as when you:
</p>

<p>
Download and use our mobile application (TIFA Sports), or any other application of ours that links to this privacy notice
</p><p>
Engage with us in other related ways, including any sales, marketing, or events
</p>

<p>
Questions or concerns? Reading this privacy notice will help you understand your privacy rights and choices. If you do not agree with our policies and practices, please do not use our Services. If you still have any questions or concerns, please contact us at info@tifasports.com.
</p>

<h3>SUMMARY OF KEY POINTS</h3>
<p>
This summary provides key points from our privacy notice, but you can find out more details about any of these topics by clicking the link following each key point or by using our table of contents below to find the section you are looking for. You can also click here to go directly to our table of contents.
</p>
<p>
<b>What personal information do we process?</b> When you visit, use, or navigate our Services, we may process personal information depending on how you interact with TIFA Sports and the Services, the choices you make, and the products and features you use. Click here to learn more.
</p>

<p>
<b>Do we receive any information from third parties?</b> We do not receive any information from third parties.
</p>

<p>
<b>How do we process your information?</b> We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law. We may also process your information for other purposes with your consent. We process your information only when we have a valid legal reason to do so. Click here to learn more.
</p>

<p>
<b>In what situations and with which parties do we share personal information?</b> We may share information in specific situations and with specific third parties. Click here to learn more.
</p>

<p>
<b>How do we keep your information safe?</b> We have organizational and technical processes and procedures in place to protect your personal information. However, no electronic transmission over the internet or information storage technology can be guaranteed to be 100% secure, so we cannot promise or guarantee that hackers, cybercriminals, or other unauthorized third parties will not be able to defeat our security and improperly collect, access, steal, or modify your information.Click here to learn more.
</p>

<p>
<b>What are your rights?</b> Depending on where you are located geographically, the applicable privacy law may mean you have certain rights regarding your personal information. Click here to learn more.
</p>

<p>
<b>How do you exercise your rights?</b> The easiest way to exercise your rights is by filling out our data subject request form available here: https://www.tifasports.com/contact/, or by contacting us. We will consider and act upon any request in accordance with applicable data protection laws.
</p>

<p>
<b>Want to learn more about what TIFA Sports does with any information we collect?</b> Click here to review the notice in full.
</p>
</>;

const TOC = () => <>
<h3>TABLE OF CONTENTS</h3>
<p>
1. WHAT INFORMATION DO WE COLLECT?<br />
2. HOW DO WE PROCESS YOUR INFORMATION?<br />
3. WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR PERSONAL INFORMATION?<br />
4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?<br />
5. HOW LONG DO WE KEEP YOUR INFORMATION?<br />
6. HOW DO WE KEEP YOUR INFORMATION SAFE?<br />
7. DO WE COLLECT INFORMATION FROM MINORS?<br />
8. WHAT ARE YOUR PRIVACY RIGHTS?<br />
9. CONTROLS FOR DO-NOT-TRACK FEATURES<br />
10. DO CALIFORNIA RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?<br />
11. DO WE MAKE UPDATES TO THIS NOTICE?<br />
12. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?<br />
13. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?
</p>
</>;

const P1 = () => <>
<h3>
1. WHAT INFORMATION DO WE COLLECT?
</h3>
<h4>
Personal information you disclose to us
</h4>

<p>
In Short: We collect personal information that you provide to us.
</p><p>
We collect personal information that you voluntarily provide to us when you register on the Services, express an interest in obtaining information about us or our products and Services, when you participate in activities on the Services, or otherwise when you contact us.
</p>

<h4>
Personal Information Provided by You.
</h4>
<p>
The personal information that we collect depends on the context of your interactions with us and the Services, the choices you make, and the products and features you use. The personal information we collect may include the following:
</p><p>
names<br />
passwords<br />
usernames<br />
age
</p>

<p>
Sensitive Information. We do not process sensitive information.
</p><p>
Application Data. If you use our application(s), we also may collect the following
</p><p>
information if you choose to provide us with access or permission:
</p><p>
Geolocation Information. We may request access or permission to track location-based information from your mobile device, either continuously or while you are using our mobile application(s), to provide certain location-based services. If you wish to change our access or permissions, you may do so in your device's settings.
</p><p>
Push Notifications. We may request to send you push notifications regarding your account or certain features of the application(s). If you wish to opt out from receiving these types of communications, you may turn them off in your device's settings.
</p><p>
This information is primarily needed to maintain the security and operation of our application(s), for troubleshooting, and for our internal analytics and reporting purposes.
</p><p>
All personal information that you provide to us must be true, complete, and accurate, and you must notify us of any changes to such personal information.
</p>
</>;

const P2 = () => <>
<h3>2. HOW DO WE PROCESS YOUR INFORMATION?</h3>
<p>
In Short: We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law. We may also process your information for other purposes with your consent.
</p><p>
We process your personal information for a variety of reasons, depending on how you interact with our Services, including:
</p><p>
To facilitate account creation and authentication and otherwise manage user accounts. We may process your information so you can create and log in to your account, as well as keep your account in working order.
</p><p>
To save or protect an individual's vital interest. We may process your information when necessary to save or protect an individual’s vital interest, such as to prevent harm.
</p>

<h3>3. WHAT LEGAL BASES DO WE RELY ON TO PROCESS YOUR INFORMATION?</h3>

<p>
In Short: We only process your personal information when we believe it is necessary and we have a valid legal reason (i.e., legal basis) to do so under applicable law, like with your consent, to comply with laws, to provide you with services to enter into or fulfill our contractual obligations, to protect your rights, or to fulfill our legitimate business interests.
</p><p>
The General Data Protection Regulation (GDPR) and UK GDPR require us to explain the valid legal bases we rely on in order to process your personal information. As such, we may rely on the following legal bases to process your personal information:
</p><p>
Consent. We may process your information if you have given us permission (i.e., consent) to use your personal information for a specific purpose. You can withdraw your consent at any time. Click here to learn more.
</p><p>
Legal Obligations. We may process your information where we believe it is necessary for compliance with our legal obligations, such as to cooperate with a law enforcement body or regulatory agency, exercise or defend our legal rights, or disclose your information as evidence in litigation in which we are involved.
</p><p>
Vital Interests. We may process your information where we believe it is necessary to protect your vital interests or the vital interests of a third party, such as situations involving potential threats to the safety of any person.
</p>

<h3>
4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
</h3>

<p>
In Short: We may share information in specific situations described in this section and/or with the following third parties.
</p><p>
We may need to share your personal information in the following situations:
</p><p>
Business Transfers. We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company.
</p>
</>

const P3 = () => <>
<h3>
5. HOW LONG DO WE KEEP YOUR INFORMATION?
</h3>

<p>
In Short: We keep your information for as long as necessary to fulfill the purposes outlined in this privacy notice unless otherwise required by law.
</p><p>
We will only keep your personal information for as long as it is necessary for the purposes set out in this privacy notice, unless a longer retention period is required or permitted by law (such as tax, accounting, or other legal requirements). No purpose in this notice will require us keeping your personal information for longer than the period of time in which users have an account with us.
</p><p>
When we have no ongoing legitimate business need to process your personal information, we will either delete or anonymize such information, or, if this is not possible (for example, because your personal information has been stored in backup archives), then we will securely store your personal information and isolate it from any further processing until deletion is possible.
</p>

<h3>
6. HOW DO WE KEEP YOUR INFORMATION SAFE?
</h3>
<p>
In Short: We aim to protect your personal information through a system of organizational and technical security measures.
</p><p>
We have implemented appropriate and reasonable technical and organizational security measures designed to protect the security of any personal information we process. However, despite our safeguards and efforts to secure your information, no electronic transmission over the Internet or information storage technology can be guaranteed to be 100% secure, so we cannot promise or guarantee that hackers, cybercriminals, or other unauthorized third parties will not be able to defeat our security and improperly collect, access, steal, or modify your information. Although we will do our best to protect your personal information, transmission of personal information to and from our Services is at your own risk. You should only access the Services within a secure environment.
</p>

<h3>
7. DO WE COLLECT INFORMATION FROM MINORS?
</h3>
<p>
In Short: We do not knowingly collect data from or market to children under 18 years of age.
</p><p>
We do not knowingly solicit data from or market to children under 18 years of age. By using the Services, you represent that you are at least 18 or that you are the parent or guardian of such a minor and consent to such minor dependent’s use of the Services. If we learn that personal information from users less than 18 years of age has been collected, we will deactivate the account and take reasonable measures to promptly delete such data from our records. If you become aware of any data we may have collected from children under age 18, please contact us at info@tifasports.com.
</p>

<h3>
8. WHAT ARE YOUR PRIVACY RIGHTS?
</h3>
<p>
In Short: In some regions, such as the European Economic Area (EEA) and United Kingdom (UK), you have rights that allow you greater access to and control over your personal information. You may review, change, or terminate your account at any time.
</p><p>
In some regions (like the EEA and UK), you have certain rights under applicable data protection laws. These may include the right (i) to request access and obtain a copy of your personal information, (ii) to request rectification or erasure; (iii) to restrict the processing of your personal information; and (iv) if applicable, to data portability. In certain circumstances, you may also have the right to object to the processing of your personal information. You can make such a request by contacting us by using the contact details provided in the section "HOW CAN YOU CONTACT US ABOUT THIS NOTICE?" below.
</p><p>
We will consider and act upon any request in accordance with applicable data protection laws.
</p><p>
If you are located in the EEA or UK and you believe we are unlawfully processing your personal information, you also have the right to complain to your local data protection supervisory authority. You can find their contact details here: https://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm.
</p><p>
If you are located in Switzerland, the contact details for the data protection authorities are available here: https://www.edoeb.admin.ch/edoeb/en/home.html.
</p><p>
Withdrawing your consent: If we are relying on your consent to process your personal information, you have the right to withdraw your consent at any time. You can withdraw your consent at any time by contacting us by using the contact details provided in the section "HOW CAN YOU CONTACT US ABOUT THIS NOTICE?" below or updating your preferences.
</p><p>
However, please note that this will not affect the lawfulness of the processing before its withdrawal nor, will it affect the processing of your personal information conducted in reliance on lawful processing grounds other than consent.
</p>

<h3>
Account Information
</h3>

<p>
If you would at any time like to review or change the information in your account or terminate your account, you can:
</p><p>
Log in to your account settings and update your user account.
</p><p>
Contact us using the contact information provided.
</p><p>
Upon your request to terminate your account, we will deactivate or delete your account and information from our active databases. However, we may retain some information in our files to prevent fraud, troubleshoot problems, assist with any investigations, enforce our legal terms and/or comply with applicable legal requirements.
</p><p>
If you have questions or comments about your privacy rights, you may email us at info@tifasports.com.
</p>
</>;

const P4 = () => <>
<h3>
9. CONTROLS FOR DO-NOT-TRACK FEATURES
</h3>
<p>
Most web browsers and some mobile operating systems and mobile applications include a Do-Not-Track ("DNT") feature or setting you can activate to signal your privacy preference not to have data about your online browsing activities monitored and collected. At this stage no uniform technology standard for recognizing and implementing DNT signals has been finalized. As such, we do not currently respond to DNT browser signals or any other mechanism that automatically communicates your choice not to be tracked online. If a standard for online tracking is adopted that we must follow in the future, we will inform you about that practice in a revised version of this privacy notice.
</p>

<h3>
10. DO CALIFORNIA RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?
</h3>
<p>
In Short: Yes, if you are a resident of California, you are granted specific rights regarding access to your personal information.
</p>
<p>
California Civil Code Section 1798.83, also known as the "Shine The Light" law, permits our users who are California residents to request and obtain from us, once a year and free of charge, information about categories of personal information (if any) we disclosed to third parties for direct marketing purposes and the names and addresses of all third parties with which we shared personal information in the immediately preceding calendar year. If you are a California resident and would like to make such a request, please submit your request in writing to us using the contact information provided below.
</p>
<p>
If you are under 18 years of age, reside in California, and have a registered account with Services, you have the right to request removal of unwanted data that you publicly post on the Services. To request removal of such data, please contact us using the contact information provided below and include the email address associated with your account and a statement that you reside in California. We will make sure the data is not publicly displayed on the Services, but please be aware that the data may not be completely or comprehensively removed from all our systems (e.g., backups, etc.).
</p>

<h3>
11. DO WE MAKE UPDATES TO THIS NOTICE?
</h3>
<p>
In Short: Yes, we will update this notice as necessary to stay compliant with relevant laws.
</p>
<p>
We may update this privacy notice from time to time. The updated version will be indicated by an updated "Revised" date and the updated version will be effective as soon as it is accessible. If we make material changes to this privacy notice, we may notify you either by prominently posting a notice of such changes or by directly sending you a notification. We encourage you to review this privacy notice frequently to be informed of how we are protecting your information.
</p>

<h3>
12. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
</h3>
<p>
If you have questions or comments about this notice, you may email us at info@tifasports.com or by post to:
</p>
<p>
TIFA SportsSpinozaweg 498Rotterdam, Zuid-Holland 3076 ET Netherlands
</p>
<h3>
13. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?
</h3>
<p>
Based on the applicable laws of your country, you may have the right to request access to the personal information we collect from you, change that information, or delete it. To request to review, update, or delete your personal information, please visit: https://www.tifasports.com/contact/.
</p>
</>;

export default () => <>
  <Intro />
  <TOC />
  <P1 />
  <P2 />
  <P3 />
</>;
